// Imports
// React and React Router
import React, { useEffect } from 'react';

// Components
import { useDarkModeContext } from '../contexts/DarkModeContext';
import { TunedCircuitLogo } from '../modules/common/TunedCircuitLogo';
import { DarkModeIcons } from '../modules/common/icons';

// Component Definition
const MaintenancePage: React.FC = () => {
  // Hooks
  const { isDark, toggleDarkMode } = useDarkModeContext();

  // Effects
  useEffect(() => {
    // Set maintenance page title
    document.title = 'TunedCircuit - Maintenance';

    // Cleanup function to restore original title when component unmounts
    return () => {
      document.title = 'TunedCircuit';
    };
  }, []);

  // Main Render
  return (
    <div className="flex h-[100dvh] items-center justify-center bg-gradient-to-br from-pink-100 to-pink-200 p-4 transition-colors duration-200 dark:from-black dark:to-gray-900">
      {/* Gradient definitions for icons */}
      <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
        <defs>
          <linearGradient id="brand-gradient-sun" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
          <linearGradient id="brand-gradient-moon" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#9333ea" />
          </linearGradient>
        </defs>
      </svg>
      <div className="relative w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl transition-colors duration-200 dark:bg-gray-900">
        {/* Dark mode toggle button */}
        <button
          onClick={toggleDarkMode}
          className="absolute right-4 top-4 rounded-lg bg-gray-200 p-2 transition-colors duration-200 hover:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700"
          aria-label="Toggle dark mode"
        >
          <DarkModeIcons isDark={isDark} className="h-6 w-6" />
        </button>

        <TunedCircuitLogo className="mb-8" />
        <div className="mb-6">
          <svg
            className="mx-auto h-16 w-16 text-pink-500 dark:text-pink-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
            />
          </svg>
        </div>
        <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
          Maintenance in Progress
        </h1>
        <p className="mb-6 text-gray-600 dark:text-gray-300">
          We&apos;re currently performing scheduled maintenance to improve your experience. Please
          check back soon.
        </p>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <p>Expected duration: 1-2 hours</p>
          <p className="mt-2">Last updated: {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

// Export
export default MaintenancePage;
